import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

const button = tv({
  base: [
    'inline-flex items-center justify-center',
    'rounded-[8px] font-medium transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2',
    'disabled:pointer-events-none',
    'relative overflow-hidden',
  ],
  variants: {
    variant: {
      // === FILL 버튼들 ===
      fillPrimary: [
        'bg-[var(--color-primary)]',
        'hover:bg-[var(--color-primary-800)]',
        'focus-visible:ring-[var(--color-primary)]',
      ],
      fillSecondary: [
        'bg-[var(--color-gray-424242)]',
        'hover:bg-[var(--color-gray-424242)]/90',
        'focus-visible:ring-[var(--color-gray-424242)]',
      ],
      fillGray: [
        'bg-[var(--color-gray-cccccc)]',
        'hover:bg-[var(--color-gray-cccccc)]/90',
        'focus-visible:ring-[var(--color-gray-cccccc)]',
      ],
      fillBlack: [
        'bg-[var(--color-grey-900)]',
        'hover:bg-[var(--color-grey-900)]/90',
        'focus-visible:ring-[var(--color-grey-900)]',
      ],

      // === OUTLINE 버튼들 ===
      outlinePrimary: ['border border-[var(--color-primary)]', 'focus-visible:ring-[var(--color-primary)]'],
      outlineGray: [
        'ap-bg-white border border-[var(--color-gray-cccccc)]',
        'hover:bg-[var(--color-gray-f5f5f5)]',
        'focus-visible:ring-[var(--color-gray-cccccc)]',
      ],
      outlineSecondary: [
        'ap-bg-white border border-[var(--color-gray-888888)]',
        'hover:bg-[var(--color-gray-f5f5f5)]',
        'focus-visible:ring-[var(--color-gray-888888)]',
      ],

      // === GHOST 버튼들 ===
      ghost: ['bg-transparent', 'hover:bg-[var(--color-primary)]/10', 'focus-visible:ring-[var(--color-primary)]'],

      // === TERTIARY 버튼들 ===
      tertiary: [
        'bg-[var(--color-primary-10)] border border-[var(--color-primary-10)]',
        'hover:bg-[var(--color-primary-100)] hover:border-[var(--color-primary-100)]',
        'active:bg-[var(--color-primary-50)] active:border-[var(--color-primary-50)]',
        'focus-visible:ring-[var(--color-primary-700)]',
      ],
      tertiaryDisabled: ['ap-bg-grey-300 ap-border-grey-300 border', 'cursor-not-allowed'],

      // === DISABLED 상태 ===
      disabled: [
        '!ap-color-gray-888888 !bg-[var(--color-gray-cccccc)] !border !border-[var(--color-gray-cccccc)]',
        'cursor-not-allowed',
      ],

      // === 하위 호환성을 위한 기존 이름들 ===
      primary: [
        'bg-[var(--color-primary)]',
        'hover:bg-[var(--color-primary)]/90',
        'focus-visible:ring-[var(--color-primary)]',
      ],
      secondary: [
        'bg-[var(--color-gray-424242)]',
        'hover:bg-[var(--color-gray-424242)]/90',
        'focus-visible:ring-[var(--color-gray-424242)]',
      ],
      gray: [
        'ap-bg-white border border-[var(--color-gray-cccccc)]',
        'hover:bg-[var(--color-gray-f5f5f5)]',
        'focus-visible:ring-[var(--color-gray-cccccc)]',
      ],
      black: [
        'bg-[var(--color-grey-900)]',
        'hover:bg-[var(--color-grey-900)]/90',
        'focus-visible:ring-[var(--color-grey-900)]',
      ],
      outline: [
        'ap-bg-white border border-[var(--color-primary)]',
        'hover:bg-[var(--color-primary)]',
        'focus-visible:ring-[var(--color-primary)]',
      ],
    },
    size: {
      null: '',
      xs: 'h-[32px] px-[12px] text-[12px] gap-[4px]',
      sm: 'h-[50px] px-[16px] text-[14px] gap-[6px]', // Small Button (Figma)
      md: 'h-[56px] px-[20px] text-[15px] gap-[8px]', // Tertiary Button (Figma)
      lg: 'h-[60px] px-[24px] text-[16px] gap-[8px]', // Primary/Secondary/Line Button (Figma)
      xl: 'h-[60px] px-[30px] text-[18px] gap-[8px]',
      xxl: 'h-[64px] px-[32px] text-[20px] gap-[10px]',
    },
    fullWidth: {
      true: 'w-full',
      false: 'w-auto',
    },
    iconPosition: {
      left: 'flex-row',
      right: 'flex-row-reverse',
      none: 'flex-row',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
    fullWidth: false,
    iconPosition: 'none',
  },
});

// Props 타입 정의
export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>,
    VariantProps<typeof button> {
  loading?: boolean;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  // 버튼 타입: fill, outline, ghost, tertiary
  buttonType?: 'fill' | 'outline' | 'ghost' | 'tertiary';
  // HTML button type (submit, reset, button)
  type?: 'submit' | 'reset' | 'button';
  // 호버 시 variant 변경 옵션
  hoverVariant?:
    | 'fillPrimary'
    | 'fillSecondary'
    | 'outlinePrimary'
    | 'outlineGray'
    | 'outlineSecondary'
    | 'disabled'
    | 'tertiary';
  width?: number;
}

// 버튼 컴포넌트
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'lg',
      fullWidth = false,
      iconPosition = 'none',
      loading = false,
      icon,
      leftIcon,
      rightIcon,
      children,
      disabled,
      buttonType = 'fill', // 기본값을 fill로 설정
      type = 'button', // HTML button type 기본값
      hoverVariant,
      width,
      ...props
    },
    ref,
  ) => {
    // 아이콘 위치 결정
    const getIconPosition = () => {
      if (icon) return 'left';
      if (leftIcon) return 'left';
      if (rightIcon) return 'right';
      return 'none';
    };

    // 실제 아이콘 위치
    const actualIconPosition = getIconPosition();

    // 타입과 variant를 조합하여 실제 variant 결정
    const getActualVariant = () => {
      if (disabled && buttonType === 'tertiary') {
        return 'tertiaryDisabled';
      }
      if (disabled) return 'disabled';

      if (buttonType === 'fill') {
        if (variant === 'disabled') return 'disabled';
        if (variant === 'secondary') return 'fillSecondary';
        if (variant === 'gray') return 'fillGray';
        if (variant === 'black') return 'fillBlack';
        return 'fillPrimary';
      } else if (buttonType === 'outline') {
        if (variant === 'disabled') return 'disabled';
        if (variant === 'secondary') return 'outlineSecondary';
        if (variant === 'gray') return 'outlineGray';
        return 'outlinePrimary';
      } else if (buttonType === 'ghost') {
        return 'ghost';
      } else if (buttonType === 'tertiary') {
        return 'tertiary';
      }

      return 'fillPrimary'; // 기본값
    };

    const actualVariant = getActualVariant();

    // 아이콘 크기 결정
    const getIconSize = () => {
      switch (size) {
        case 'xs':
          return 'w-[16px] h-[16px]';
        case 'sm':
          return 'w-[18px] h-[18px]';
        case 'md':
          return 'w-[20px] h-[20px]';
        case 'lg':
          return 'w-[24px] h-[24px]';
        case 'xl':
          return 'w-[28px] h-[28px]';
        case 'xxl':
          return 'w-[32px] h-[32px]';
        default:
          return 'w-[24px] h-[24px]';
      }
    };

    // 호버 variant 클래스 생성
    const getHoverClasses = () => {
      if (!hoverVariant || disabled) return '';

      const hoverClasses: Record<string, string> = {
        primary: 'hover:bg-[var(--color-primary)] hover:ap-color-white hover:border-[var(--color-primary)]',
        secondary: 'hover:bg-[var(--color-gray-424242)] hover:ap-color-white hover:border-[var(--color-gray-424242)]',
        outline: 'hover:bg-[var(--color-primary)] hover:ap-color-white hover:border-[var(--color-primary)]',
        outlineGray: 'hover:bg-[var(--color-gray-f5f5f5)] hover:text-[var(--color-gray-424242)]',
        outlineSecondary: 'hover:bg-[var(--color-gray-f5f5f5)] hover:text-[var(--color-gray-424242)]',
        ghost: 'hover:bg-[var(--color-primary)] hover:ap-color-white hover:border-transparent',
        tertiary:
          'hover:bg-[var(--color-primary-100)] hover:border-[var(--color-primary-100)] hover:ap-color-primary-900',
      };

      return hoverClasses[hoverVariant] || '';
    };

    return (
      <button
        ref={ref}
        type={type}
        style={width ? { width: `${width}px` } : undefined}
        className={`${button({
          variant: actualVariant,
          size,
          fullWidth,
          iconPosition: actualIconPosition,
          className: '',
        })} ${getHoverClasses()} ${className || ''}`}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className={`${getIconSize()} animate-spin`}>
            <svg className='w-full h-full' viewBox='0 0 24 24' fill='none'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          </div>
        )}

        {/* 아이콘들 */}
        {(icon || leftIcon) && !loading && <div className='flex items-center justify-center'>{icon || leftIcon}</div>}

        {/* 텍스트 */}
        {children && (
          <span
            className={`ap-text-body2-sb-0 ${
              actualVariant === 'fillPrimary' ||
              actualVariant === 'fillSecondary' ||
              actualVariant === 'fillGray' ||
              actualVariant === 'fillBlack'
                ? 'ap-color-white'
                : actualVariant === 'disabled'
                  ? '!text-[#888888]'
                  : actualVariant === 'outlinePrimary'
                    ? 'ap-color-primary hover:ap-color-white'
                    : actualVariant === 'tertiary'
                      ? 'ap-color-primary-700 hover:ap-color-primary-900 active:ap-color-primary-900'
                      : actualVariant === 'tertiaryDisabled'
                        ? 'ap-color-grey-400'
                        : actualVariant === 'outlineGray'
                          ? 'ap-color-gray-424242'
                          : 'ap-color-gray-424242'
            }`}
          >
            {children}
          </span>
        )}

        {/* 오른쪽 아이콘 */}
        {rightIcon && !loading && (
          <div className={`${getIconSize()} flex items-center justify-center`}>{rightIcon}</div>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

// 사용 예시:
/*
// 기본 Fill 버튼 (buttonType="fill"이 기본값)
<Button>확인</Button>

// Fill 버튼들
<Button buttonType="fill" variant="primary">Primary Fill</Button>
<Button buttonType="fill" variant="secondary">Secondary Fill</Button>

// Outline 버튼들
<Button buttonType="outline" variant="primary">Primary Outline</Button>
<Button buttonType="outline" variant="gray">Gray Outline</Button>
<Button buttonType="outline" variant="secondary">Secondary Outline</Button>

// 아이콘이 있는 버튼
<Button buttonType="fill" icon={<SomeIcon />}>내 병원 협상 신청하기</Button>

// 다양한 사이즈
<Button buttonType="fill" size="xs">작은 버튼</Button>
<Button buttonType="outline" size="xl">큰 버튼</Button>

// 전체 너비 버튼
<Button buttonType="fill" fullWidth>전체 너비</Button>

// 비활성화 상태
<Button buttonType="fill" disabled>비활성화</Button>

// 로딩 상태
<Button buttonType="fill" loading>로딩 중...</Button>
*/
